<template>
  <div class="viq-expert">
    <div class="viq-expert__header">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M8 1l2 3 3.5 1-2.5 2.5.5 3.5L8 9.5 4.5 11l.5-3.5L2.5 5 6 4l2-3z" fill="currentColor" />
      </svg>
      <span>Expert Commentary</span>
      <el-tooltip effect="dark" placement="top" :open-delay="300">
        <div slot="content">In-depth analysis from VIXIO's regulatory<br>specialists related to your question.</div>
        <span class="viq-expert__help-icon">?</span>
      </el-tooltip>
    </div>
    <div class="viq-expert__list">
      <div
        v-for="(article, i) in articles"
        :key="i"
        class="viq-expert__item"
      >
        <div class="viq-expert__badges">
          <span v-if="article.classification" class="viq-expert__badge" :class="classificationClass(article.classification)">
            {{ article.classification }}
          </span>
          <span v-if="article.jurisdiction" class="viq-expert__badge viq-expert__badge--jurisdiction">
            {{ article.jurisdiction }}
          </span>
        </div>
        <h5 class="viq-expert__item-title">
          <a
            v-if="article.platform_url"
            :href="localUrl(article.platform_url)"
            target="_blank"
            rel="noopener noreferrer"
            class="viq-expert__item-link"
          >{{ article.title }}</a>
          <span v-else>{{ article.title }}</span>
        </h5>
        <p v-if="articleExcerpt(article)" class="viq-expert__item-excerpt">
          {{ articleExcerpt(article) }}
        </p>
        <div class="viq-expert__item-footer">
          <span v-if="article.published_date" class="viq-expert__item-date">
            {{ formatDate(article.published_date) }}
          </span>
          <a
            v-if="article.platform_url"
            :href="localUrl(article.platform_url)"
            target="_blank"
            rel="noopener noreferrer"
            class="viq-expert__item-view"
          >
            View full article
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M5 1h6v6M11 1L5 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViqExpertCommentary",
  props: {
    articles: {
      type: Array,
      required: true,
    },
  },
  methods: {
    articleExcerpt(article) {
      const text = article.summary || article.content || article.excerpt || "";
      if (!text) return "";
      // Truncate to ~200 chars
      return text.length > 200 ? text.substring(0, 200).replace(/\s+\S*$/, "") + "..." : text;
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    localUrl(url) {
      if (!url) return "/";
      try {
        const parsed = new URL(url);
        return parsed.pathname + parsed.search + parsed.hash;
      } catch (e) {
        return url;
      }
    },
    classificationClass(classification) {
      const map = {
        Actionable: "viq-expert__badge--actionable",
        Indicative: "viq-expert__badge--indicative",
        Informative: "viq-expert__badge--informative",
      };
      return map[classification] || "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-expert {
  margin-top: $viq-space-md;
  padding: $viq-space-md;
  background: rgba(37, 99, 235, 0.03);
  border: 1px solid rgba(37, 99, 235, 0.1);
  border-radius: $viq-radius-md;

  &__header {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    font-size: $viq-font-sm;
    font-weight: 700;
    color: $viq-accent;
    margin-bottom: $viq-space-md;
  }

  &__help-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid $viq-text-muted;
    color: $viq-text-muted;
    font-size: 9px;
    font-weight: 700;
    cursor: help;
    line-height: 1;
    flex-shrink: 0;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $viq-space-sm;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: $viq-space-xs;
  }

  &__badge {
    display: inline-flex;
    padding: 1px 6px;
    border-radius: $viq-radius-sm;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    background: #f1f5f9;
    color: #475569;

    &--jurisdiction {
      background: $viq-bg-secondary;
      color: $viq-text-secondary;
    }
    &--actionable {
      background: #fee2e2;
      color: #b91c1c;
    }
    &--indicative {
      background: #fef3c7;
      color: #92400e;
    }
    &--informative {
      background: #e0f2fe;
      color: #0369a1;
    }
  }

  &__item {
    padding: $viq-space-md;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;
    transition: border-color $viq-transition-fast;

    &:hover {
      border-color: $viq-accent;
    }

    &-title {
      font-size: $viq-font-base;
      font-weight: 600;
      color: $viq-text-primary;
      margin: 0 0 $viq-space-xs;
      line-height: 1.4;
    }

    &-link {
      color: $viq-text-primary;
      text-decoration: none;

      &:hover {
        color: $viq-accent;
        text-decoration: underline;
      }
    }

    &-excerpt {
      font-size: $viq-font-sm;
      color: $viq-text-secondary;
      margin: 0 0 $viq-space-sm;
      line-height: 1.5;
    }

    &-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $viq-space-sm;
    }

    &-date {
      font-size: $viq-font-xs;
      color: $viq-text-muted;
    }

    &-view {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: $viq-font-xs;
      font-weight: 600;
      color: $viq-accent;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}
</style>
